import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getLensSelections, setLensSelection, removeLensSelection, LensData } from '../utils/lensStore';
import LensSelectionModal from '../components/LensSelectionModal';

const SKIP_LENS_CATEGORIES = [3, 4, 5, 6]; // Sunglasses, Contact Lenses, Accessories, Blue Light Glasses

const fmtLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const buildLensLabel = (lens: LensData): string => {
  if (!lens.lensType) return '';
  const base = fmtLabel(lens.lensType);
  const coat = lens.coating && lens.coating !== 'normal' ? ` + ${fmtLabel(lens.coating)}` : '';
  return `${base}${coat}`;
};

const CheckPage: React.FC = () => {
  const { cartItems, cartTotal, removeFromCart, updateQuantity } = useCart();
  const { user, openLogin } = useAuth();
  const navigate = useNavigate();

  const [lensSelections, setLensSelections] = useState<Record<string, LensData>>(
    () => getLensSelections()
  );
  const [lensModalItem, setLensModalItem] = useState<{ id: string; name: string } | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.check-type-dropdown')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const frameItems = cartItems.filter(
    (item) => !SKIP_LENS_CATEGORIES.includes(item.category_id ?? 0)
  );
  // Frame Only = user explicitly chose no lens; also counts as a valid "selection"
  const allFramesHaveLens = frameItems.length === 0 || frameItems.every((item) => !!lensSelections[item.id]);

  const handleFrameOnly = (cartItemId: string) => {
    setLensSelection(cartItemId, {
      lensType: 'frame_only',
      coating: '',
      eyePower: { leftEye: { sphere: '', cylinder: '', axis: '' }, rightEye: { sphere: '', cylinder: '', axis: '' } },
      hasPrescription: false,
      coatingPrice: 0,
    });
    setLensSelections(getLensSelections());
  };

  const totalLensPrice = frameItems.reduce((sum, item) => {
    return sum + (lensSelections[item.id]?.coatingPrice ?? 0);
  }, 0);

  const handleLensModalClose = () => {
    setLensSelections(getLensSelections());
    setLensModalItem(null);
  };

  if (!user) {
    return (
      <div className="checkout-page">
        <div className="empty-state">
          <div className="empty-state__icon">🛒</div>
          <h2>Please sign in to continue</h2>
          <p>You need to be logged in to view your cart and checkout.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn--primary" onClick={() => openLogin()}>Log In</button>
            <Link to="/products" className="btn btn--outline">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <div className="empty-state">
          <div className="empty-state__icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Add some eyewear to get started!</p>
          <Link to="/products" className="btn btn--primary">Shop Now</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb__sep">›</span>
        <Link to="/cart">Cart</Link>
        <span className="breadcrumb__sep">›</span>
        <span>Checkout</span>
      </div>

      <div className="checkout-layout">
        {/* Left: Cart items with per-item lens selection */}
        <div className="check-cart-panel">
          <div className="check-cart-header">
            <h2 className="check-cart-title">My Cart</h2>
            <span className="check-cart-count">
              {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="check-cart-items">
            {cartItems.map((item, index) => {
              const needsLens = !SKIP_LENS_CATEGORIES.includes(item.category_id ?? 0);
              const lensData = lensSelections[item.id];

              return (
                <div key={item.id}>
                  <div className="check-cart-item">
                    <img
                      src={item.image_uri?.[0] || ''}
                      alt={item.product_name}
                      className="check-cart-item__img"
                    />
                    <div className="check-cart-item__info">
                      <p className="check-cart-item__name">{item.product_name}</p>
                      <p className="check-cart-item__price">₹{item.selling_price.toLocaleString()}</p>

                      {/* Selected lens display — shown only when a lens is chosen */}
                      {needsLens && lensData && (
                        <div className="check-lens-row check-lens-row--selected">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <circle cx="7" cy="12" r="4" /><circle cx="17" cy="12" r="4" /><line x1="11" y1="12" x2="13" y2="12" />
                          </svg>
                          <span className="check-lens-label">
                            {lensData.lensType === 'frame_only' ? 'Frame Only' : (
                              <>
                                {buildLensLabel(lensData)}
                                {lensData.coatingPrice > 0 && (
                                  <span className="check-lens-price"> · ₹{lensData.coatingPrice.toLocaleString()}</span>
                                )}
                              </>
                            )}
                          </span>
                          {lensData.lensType !== 'frame_only' && (
                            <button className="check-lens-edit" onClick={() => setLensModalItem({ id: item.id, name: item.product_name })}>Edit</button>
                          )}
                          <button
                            className="check-lens-edit"
                            style={{ marginLeft: 4 }}
                            onClick={() => { removeLensSelection(item.id); setLensSelections(getLensSelections()); }}
                          >
                            Change
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="check-cart-item__right">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {needsLens && !lensData && (
                          <div className="check-type-dropdown">
                            <button
                              className="check-type-dropdown__btn"
                              onClick={() => setOpenDropdownId(openDropdownId === item.id ? null : item.id)}
                            >
                              <span>Select type</span>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </button>
                            {openDropdownId === item.id && (
                              <div className="check-type-dropdown__menu">
                                <button
                                  className="check-type-dropdown__item"
                                  onClick={() => { setLensModalItem({ id: item.id, name: item.product_name }); setOpenDropdownId(null); }}
                                >
                                  Select Lens
                                </button>
                                <button
                                  className="check-type-dropdown__item"
                                  onClick={() => { handleFrameOnly(item.id); setOpenDropdownId(null); }}
                                >
                                  Frame Only
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="quantity-stepper quantity-stepper--sm">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >−</button>
                          <span>{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            disabled={item.quantity >= ((item as any).available_quantity || 99)}
                          >+</button>
                        </div>
                      </div>
                      <button
                        className="check-cart-item__remove"
                        onClick={() => removeFromCart(item.id)}
                        title="Remove"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                        Remove
                      </button>
                    </div>
                  </div>
                  {index < cartItems.length - 1 && <div className="check-cart-divider" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Price Details */}
        <div className="order-summary checkout-summary">
          <h3 className="order-summary__title">Price Details</h3>

          <div className="order-summary__rows">
            <div className="order-summary__row">
              <span>Price ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})</span>
              <span>₹{Math.round(cartTotal).toLocaleString()}</span>
            </div>
            {totalLensPrice > 0 && (
              <div className="order-summary__row">
                <span>Lens Customization</span>
                <span>₹{totalLensPrice.toLocaleString()}</span>
              </div>
            )}
            <div className="order-summary__row">
              <span>Delivery Charges</span>
              <span style={{ color: '#16a34a', fontWeight: 600 }}>Free</span>
            </div>
          </div>

          <div className="order-summary__total">
            <span>Total Amount</span>
            <span>₹{Math.round(cartTotal + totalLensPrice).toLocaleString()}</span>
          </div>

          {frameItems.length > 0 && !allFramesHaveLens && (
            <div className="check-lens-hint">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Select a lens type for each frame to enable checkout.
            </div>
          )}

          <button
            className="btn btn--primary order-summary__checkout-btn"
            onClick={() => navigate('/address')}
            disabled={!allFramesHaveLens}
          >
            Proceed to Buy
          </button>
        </div>
      </div>

      {/* Per-item Lens Selection Modal */}
      {lensModalItem && (
        <LensSelectionModal
          cartItemId={lensModalItem.id}
          productName={lensModalItem.name}
          initialData={lensSelections[lensModalItem.id]}
          onClose={handleLensModalClose}
        />
      )}
    </div>
  );
};

export default CheckPage;
